import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import WayfinderEditor from './WayfinderEditor';
import UseToast from '../UseToast';
import useS3Upload from '../../hooks/useS3Upload';
import useCurrentLanguage from '../../hooks/useCurrentLanguage';

const WayfinderSubCategory = () => {
  const navigate = useNavigate();
  const { userId, subCategoryId } = useParams();
  const { showErrorToast, showSuccessToast } = UseToast();
  
  // Language management
  const { currentLanguage } = useCurrentLanguage(userId);

  const [loading, setLoading] = useState(false);
  const [buttonColor, setButtonColor] = useState('#000000');
  const [iconFile, setIconFile] = useState(null);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [clickSoundFile, setClickSoundFile] = useState(null);
  const [pngFile, setPngFile] = useState(null);
  const [subcategories, setSubcategories] = useState([]);
  const [locations, setLocations] = useState([]);
  const [selectedExistingSubCategories, setSelectedExistingSubCategories] = useState([]);

  // Step-by-Step Directions State
  const [stepByStepConfig, setStepByStepConfig] = useState({
    useGlobal: true,
    useParent: false,
    selectedParentId: null,
    codeInsert1: '',
    opening: '',
    mapTimeDelay: null,
    codeInsert2: '',
    stepByStepOpening: '',
    stepByStepDirections: '',
    codeInsert3: '',
    closing: '',
    codeInsert4: '',
    globalQrClosing: {
      enabled: true,
      text: '',
    },
    codeInsert5: '',
  });

  // Parent categories for subcategory
  const [parentCategories, setParentCategories] = useState([]);

  const [item, setItem] = useState({
    name: '',
    description: '',
    status: true,
    enabled: true,
    buttonText: '',
    parentCategories: [],

    // globalSettings: {
    //   cornerRadius: 0,
    //   height: 50,
    //   width: 150,
    //   outlineColor: '#000000',
    //   outlineThickness: 1,
    //   fillColor: '#ffffff',
    //   font: 'Arial',
    //   fontColor: '#127E10',
    //   fontSize: 16,
    //   textAlign: 'center',
    //   marginBottom: 2,
    //   lineHeight: 1,
    //   padding: 10,
    //   shadow: {
    //     angle: 5,
    //     size: 10,
    //     distance: 5,
    //     alpha: 0.5,
    //     color: '#FF5733',
    //   },
    //   highlight: {
    //     borderColor: '#000000',
    //     fillColor: '#ffffff',
    //     fontColor: '#000000',
    //   },
    //   clickSound: '',
    //   clickSoundFilename: null,
    //   clickSoundVolume: 1,
    // },

    customSettings: {
      cornerRadius: 0,
      height: 50,
      width: 150,
      outlineColor: '#000000',
      outlineThickness: 1,
      fillColor: '#ffffff',
      font: 'Arial',
      fontColor: '#127E10',
      fontSize: 16,
      textAlign: 'center',
      marginBottom: 2,
      lineHeight: 1,
      padding: 10,
      shadow: {
        angle: 5,
        size: 10,
        distance: 5,
        alpha: 0.5,
        color: '#FF5733',
      },
      highlight: {
        borderColor: '#000000',
        fillColor: '#ffffff',
        fontColor: '#000000',
      },
      clickSound: '',
      clickSoundFilename: null,
      clickSoundVolume: 1,

      // Custom-only fields
      pngBtnenabled: false,
      hideBtnText: false,
      btnPng: null,
      btnPngFileName: null,
    },
  });

  const { uploadFile: uploadToS3, uploading: s3Uploading } = useS3Upload({
    signedUrlEndpoint: '/generate-signed-url',
    onSuccess: (fileUrl, file) => {
      showSuccessToast(`${file.name} uploaded successfully`);
    },
    onError: () => {
      showErrorToast('Upload failed');
    },
  });

  useEffect(() => {
    if (userId && subCategoryId) {
      fetchItemData();
      fetchSiteBrandColor();
      fetchCategories();
      fetchSubcategories();
      fetchLocations();
      fetchStepByStepConfig();
      fetchParentCategoriesForStepByStep();
    }
  }, [userId, subCategoryId]);

  // Fetch all subcategories (for navigation mapping)
  const fetchSubcategories = async () => {
    try {
      const response = await axios.get(`/api/wayfinder/subcategory/${userId}`);
      if (response.data.success) {
        setSubcategories(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching subcategories for navigation:', error);
    }
  };

  // Fetch all locations (for navigation mapping)
  const fetchLocations = async () => {
    try {
      const res = await axios.get(`/api/wayfinder/get-locations/${userId}`);
      if (res.data.success) {
        setLocations(res.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching locations for navigation:', error);
    }
  };

  // Fetch Step-by-Step Directions Configuration
  const fetchStepByStepConfig = async () => {
    try {
      const response = await axios.get(`/api/wayfinder/step-by-step-directions/subcategory/${userId}/${subCategoryId}`);
      if (response.data.success) {
        setStepByStepConfig(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching step-by-step directions:", error);
    }
  };

  // Save Step-by-Step Directions Configuration
  const saveStepByStepConfig = async () => {
    try {
      await axios.post(`/api/wayfinder/step-by-step-directions/subcategory/${userId}/${subCategoryId}`, stepByStepConfig);
      showSuccessToast("Step-by-step directions configuration saved successfully!");
    } catch (error) {
      console.error("Error saving step-by-step directions:", error);
      showErrorToast("Failed to save step-by-step directions configuration");
    }
  };

  // Fetch parent categories for step-by-step directions
  const fetchParentCategoriesForStepByStep = async () => {
    try {
      // Get all categories
      const categoriesRes = await axios.get(`/api/wayfinder/get-categories/${userId}`);
      const allCategories = categoriesRes.data?.data || [];

      // Filter categories that contain this subcategory
      const parentCats = allCategories.filter(cat => 
        cat.navigation?.subcategories?.some(sub => String(sub._id || sub) === subCategoryId) ||
        cat.subCategories?.some(sub => String(sub._id || sub) === subCategoryId)
      ).map(cat => ({
        _id: cat._id,
        name: cat.name?.en || cat.buttonText?.en || 'Unnamed Category',
        type: 'Category',
        mapTimeDelay: null,
      }));

      // Fetch step-by-step configs for each parent to get mapTimeDelay
      const parentsWithDelay = await Promise.all(
        parentCats.map(async (parent) => {
          try {
            const configRes = await axios.get(`/api/wayfinder/step-by-step-directions/category/${userId}/${parent._id}`);
            return {
              ...parent,
              mapTimeDelay: configRes.data?.data?.mapTimeDelay,
            };
          } catch (err) {
            return parent;
          }
        })
      );

      setParentCategories(parentsWithDelay);
    } catch (error) {
      console.error("Error fetching parent categories:", error);
    }
  };

  const resolvedButtonSettings = useMemo(() => {
    if (item?.enabled) {
      return {
        ...item.globalSettings,
        ...item.customSettings,
      };
    }
    return item.globalSettings;
  }, [item?.globalSettings, item?.customSettings, item?.enabled]);

  const fetchSiteBrandColor = async () => {
    try {
      const response = await axios.get(`/api/site-brand-color/${userId}`);
      if (!response.data.error) {
        setButtonColor(response.data?.siteBrandColor || '#000000');
      }
    } catch (error) {
      console.error('Error fetching brand color:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoadingCategories(true);
      const response = await axios.get(`/api/wayfinder/get-categories/${userId}`);

      if (response.data.success) {
        const categoriesData = response.data.data;
        setCategories(
          Array.isArray(categoriesData) ? categoriesData : categoriesData ? [categoriesData] : []
        );
      }
    } catch (error) {
      showErrorToast('Failed to load categories');
    } finally {
      setLoadingCategories(false);
    }
  };

  const fetchItemData = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`/api/wayfinder/subcategory/${userId}`);

      if (response.data.success) {
        const dataArr = response.data.data || [];
        const data = dataArr.find(curElem => curElem._id === subCategoryId);

        const parentCategories = data?.parentCategories || [];
        const formattedParentCategories = Array.isArray(parentCategories)
          ? parentCategories.map(curElem => curElem._id)
          : [];

        setItem(prev => ({
          ...prev,
          ...data,
          parentCategories: formattedParentCategories,
          textStyle: { ...prev.textStyle, ...data?.textStyle },
          buttonStyle: { ...prev.buttonStyle, ...data?.buttonStyle },
          iconStyle: { ...prev.iconStyle, ...data?.iconStyle },
          useCustomIcon: data?.useCustomIcon !== false,
          buttonFileUrl: data?.buttonFileUrl || '',
          // Merge global/custom settings with defaults to avoid undefined
          globalSettings: {
            ...(data?.globalSettings || {}),
            shadow: {
              ...(data?.globalSettings?.shadow || {}),
            },
            highlight: {
              ...(data?.globalSettings?.highlight || {}),
            },
          },
          customSettings: {
            ...prev.customSettings,
            ...(data?.customSettings || {}),
            shadow: {
              ...prev.customSettings.shadow,
              ...(data?.customSettings?.shadow || {}),
            },
            highlight: {
              ...prev.customSettings.highlight,
              ...(data?.customSettings?.highlight || {}),
            },
          },
          navigation: {
            directLocation: data?.navigation?.directLocation ?? false,
            type: data?.navigation?.type || 'none',
            subcategories: data?.navigation?.subcategories || [],
            locations: data?.navigation?.locations || [],
          },
        }));
      } else {
        showErrorToast('SubCategory not found');
        navigate(-1);
      }
    } catch (error) {
      showErrorToast('Failed to load subcategory data');
    } finally {
      setLoading(false);
    }
  };

  const { getRootProps: getIconRootProps, getInputProps: getIconInputProps } = useDropzone({
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif', '.svg'] },
    maxFiles: 1,
    onDrop: acceptedFiles => {
      const file = acceptedFiles[0];
      setIconFile(file);
      const fileUrl = URL.createObjectURL(file);
      setItem(prev => ({ ...prev, icon: fileUrl }));
    },
  });

  const { getRootProps: getPngRootProps, getInputProps: getPngInputProps } = useDropzone({
    accept: { 'image/*': ['.png'] },
    maxFiles: 1,
    onDrop: acceptedFiles => {
      const file = acceptedFiles[0];
      console.log('fle000000000', file);
      setPngFile(file);
      const fileUrl = URL.createObjectURL(file);
      setItem(prev => ({
        ...prev,
        buttonFileUrl: fileUrl,
        buttonFileName: file.name || 'File Name',
      }));
    },
  });

  const handleUploadPng = async () => {
    console.log('Png file selected', pngFile);

    if (!pngFile) {
      showErrorToast('No PNG file selected');
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(pngFile);

      setItem(prev => ({
        ...prev,
        customSettings: {
          ...prev.customSettings,
          btnPng: fileUrl,
          btnPngFileName: pngFile.name,
          pngBtnenabled: true,
        },
      }));

      // Clear the file after upload
      setPngFile(null);
    } catch (error) {
      showErrorToast('Failed to upload PNG');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClickSound = async () => {
    console.log('Click sound file selected', clickSoundFile);

    if (!clickSoundFile) {
      showErrorToast('No click sound file selected');
      return;
    }

    try {
      setLoading(true);
      const fileUrl = await uploadToS3(clickSoundFile);

      setItem(prev => ({
        ...prev,
        customSettings: {
          ...prev.customSettings,
          clickSound: fileUrl,
          clickSoundFilename: clickSoundFile.name,
        },
      }));

      // Clear the file after upload
      setClickSoundFile(null);
    } catch (error) {
      showErrorToast('Failed to upload click sound');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const payload = {
        userId,
        ...item,
        parentCategories: item.parentCategories || [],
        globalSettings: item.globalSettings || {},
        customSettings: item.customSettings || {},
        currentLanguage: currentLanguage, // Pass current language to backend
      };

      console.log("payload", payload);

      const response = await axios.patch(`/api/wayfinder/subcategory/${subCategoryId}`, payload);

      if (response.data.success) {
        showSuccessToast('SubCategory settings saved successfully!');
        fetchItemData();
      } else {
        showErrorToast(response.data.message || 'Failed to save');
      }
    } catch (error) {
      showErrorToast('Failed to save subcategory settings');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = () => {
    if (
      window.confirm(
        'Are you sure you want to delete this subcategory? This action cannot be undone.'
      )
    ) {
      axios
        .delete(`/api/wayfinder/subcategory/${subCategoryId}?userId=${userId}`)
        .then(response => {
          if (response.data.success) {
            showSuccessToast('SubCategory deleted successfully!');
            navigate(`/wayfinder-main/${userId}`);
          } else {
            showErrorToast(response.data.message || 'Failed to delete');
          }
        })
        .catch(error => {
          showErrorToast('Failed to delete subcategory');
        });
    }
  };

  return (
    <WayfinderEditor
      userId={userId}
      type="subcategory"
      item={item}
      setItem={setItem}
      resolvedButtonSettings={resolvedButtonSettings}
      loading={loading || s3Uploading}
      buttonColor={buttonColor}
      iconFile={iconFile}
      pngFile={pngFile}
      getIconRootProps={getIconRootProps}
      getIconInputProps={getIconInputProps}
      getPngRootProps={getPngRootProps}
      getPngInputProps={getPngInputProps}
      handleUploadPng={handleUploadPng}
      handleSave={handleSave}
      handleDelete={handleDelete}
      btnPng={pngFile}
      setBtnPng={setPngFile}
      handleUploadClickSound={handleUploadClickSound}
      clickSoundFile={clickSoundFile}
      setClickSoundFile={setClickSoundFile}
      subcategories={subcategories}
      locations={locations}
      selectedExistingSubCategories={selectedExistingSubCategories}
      setSelectedExistingSubCategories={setSelectedExistingSubCategories}
      currentLanguage={currentLanguage}
      stepByStepConfig={stepByStepConfig}
      setStepByStepConfig={setStepByStepConfig}
      saveStepByStepConfig={saveStepByStepConfig}
      parentOptions={parentCategories}
    />
  );
};

export default WayfinderSubCategory;
